import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Drawer,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Modal,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { DataGrid } from '@mui/x-data-grid';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import ToastMsg from '../../../Components/ToastMsg/ToastMsg';
import { ImagePaths } from '../../../Utility/ImagePaths';
import RestAPIService from '../../../Utility/Services/RestAPIService';
import { Serviceurls } from '../../../Utility/API/Serviceurls';
import {
  AmountFormat,
  CheckAccessFunc,
  formatDateTime
} from '../../../Components/CommonFunctions/CommonFunctions';
import { localGetItem } from '../../../Utility/Services/CacheProviderService';
import { CurrencySymbol } from '../../../Utility/Constants';
import SearchIcon from '@mui/icons-material/Search';
import { Colors } from '../../../Styles/Colors';
import { CommonPopUp } from '../../../Components/Common Components/CommonComponents';
import PharmaInventoryAuditLogEditModal from './PharmaInventoryAuditLogEditModal';
import EditStockPopUp from '../PharmacySettings/EditStockPopUp';
import '../PharmacySettings/PharmaSettings.css';
import '../../Laboratory/Inventory/inventory.css';

const dosageOptionLabel = (option) => {
  if (option == null || option === '') return '';
  if (typeof option === 'string') return option;
  return (
    option.dosage_type ||
    option.dosagetype_name ||
    option.name ||
    String(option.dosagetype_id ?? '')
  );
};

const dosageTypeQueryValue = (selected) => {
  if (selected == null) return '';
  if (typeof selected === 'string') return selected;
  return (
    selected.dosage_type ||
    selected.dosagetype_name ||
    selected.name ||
    ''
  );
};

/** Audit history "Updated Date & Time": same as formatDateTime with | between date and time */
const formatModifiedDateTimeWithPipe = (inputDate) => {
  if (inputDate == null || inputDate === '') return '-';
  const s = formatDateTime(inputDate);
  if (!s || s === '-') return s;
  const m = String(s).match(/^(\d{2}-\d{2}-\d{4})\s+(.+)$/);
  return m ? `${m[1]} | ${m[2]}` : s;
};

const sortEventsNewestFirst = (events) => {
  const list = Array.isArray(events) ? [...events] : [];
  list.sort((a, b) => {
    const da = new Date(
      a?.event_date || a?.created_date || a?.modified_date || 0
    ).getTime();
    const db = new Date(
      b?.event_date || b?.created_date || b?.modified_date || 0
    ).getTime();
    return db - da;
  });
  return list;
};

const HISTORY_CHANGE_LABELS = {
  drug_name: 'Brand name',
  batch_no: 'Batch no.',
  available_quantity: 'Quantity',
  mrp_per_quantity: 'MRP / qty',
  expiry_date: 'Expiry',
  reorder_threshold: 'Reorder threshold',
  schedule: 'Schedule',
  drug_strength: 'Strength',
  sales_discount_for_bill: 'Sales discount %',
  generic_name: 'Generic name',
  dosage_type: 'Dosage type',
  grouping: 'Grouping',
  physical_box_no: 'Physical box',
  hsn_code: 'HSN code',
  expiry_alert_period: 'Expiry alert period',
  reason: 'Reason',
  comments: 'Reason',
  remarks: 'Reason',
  correction_reason: 'Reason'
};

const pairFromHistoryChange = (val) => {
  if (val == null) return ['', ''];
  if (Array.isArray(val)) {
    return [val[0] ?? '', val[1] ?? ''];
  }
  if (typeof val === 'object') {
    return [
      val.from ?? val.old ?? val.previous ?? val.before ?? val.from_value ?? '',
      val.to ?? val.new ?? val.next ?? val.after ?? val.to_value ?? ''
    ];
  }
  return ['', String(val)];
};

const isReasonChangedFieldKey = (key) =>
  key === 'reason' ||
  key === 'comments' ||
  key === 'remarks' ||
  key === 'correction_reason';

const formatHistoryChangedFields = (changed_fields) => {
  if (!changed_fields || typeof changed_fields !== 'object') {
    return '-';
  }
  const entries = Object.entries(changed_fields);
  const primary = entries.filter(([k]) => !isReasonChangedFieldKey(k));
  const trailing = entries.filter(([k]) => isReasonChangedFieldKey(k));
  const parts = [];
  for (const [key, val] of [...primary, ...trailing]) {
    const [fromV, toV] = pairFromHistoryChange(val);
    const label =
      HISTORY_CHANGE_LABELS[key] ||
      String(key).replace(/_/g, ' ');
    const fromStr =
      fromV === '' || fromV == null ? '—' : String(fromV);
    const toStr = toV === '' || toV == null ? '—' : String(toV);
    parts.push(`${label}: ${fromStr} → ${toStr}`);
  }
  return parts.length ? parts.join('\n') : '-';
};

const HISTORY_CHANGED_PREVIEW_MAX_LINES = 2;
const HISTORY_CHANGED_TOOLTIP_MIN_LINE_CHARS = 72;

const changedFieldsLines = (changed_fields) => {
  const text = formatHistoryChangedFields(changed_fields);
  if (text === '-') return [];
  return text.split('\n').filter((line) => String(line).trim().length > 0);
};

const WhatChangedBulletedTooltip = ({ lines }) => (
  <Box
    component="ul"
    sx={{
      m: 0,
      pl: 2.25,
      py: 0.25,
      maxWidth: 520,
      listStyle: 'disc'
    }}
  >
    {lines.map((line, i) => (
      <Typography
        component="li"
        key={i}
        variant="body2"
        sx={{
          mb: 0.35,
          display: 'list-item',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word'
        }}
      >
        {line}
      </Typography>
    ))}
  </Box>
);

const historyTruncatedCellSx = {
  lineHeight: 1.35,
  display: 'block',
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};

/** Span root + MUI Tooltip so truncated cells do not rely on native browser title tooltips */
function HistoryTruncatedCell({ value, longAt = 20, sliceAt = 15 }) {
  const s = value == null || value === '' ? '' : String(value);
  if (!s) {
    return (
      <Box component="span" sx={historyTruncatedCellSx}>
        -
      </Box>
    );
  }
  const needsTooltip = s.length > longAt;
  const display = needsTooltip ? `${s.slice(0, sliceAt)}…` : s;
  const inner = (
    <Box component="span" sx={historyTruncatedCellSx}>
      {display}
    </Box>
  );
  if (!needsTooltip) return inner;
  return (
    <Tooltip
      title={s}
      placement="top"
      arrow
      componentsProps={{
        tooltip: { sx: { maxWidth: 360 } }
      }}
    >
      {inner}
    </Tooltip>
  );
}

const mapAuditRowForViewDrug = (row) => {
  if (!row) return {};
  const qty =
    row.available_quantity ??
    row.stock_quantity ??
    row.stock_in_quantity ??
    '';
  return {
    ...row,
    drug_strength: row.drug_strength ?? row.strength ?? row.dosage_strength ?? '',
    schedules: row.schedules ?? row.schedule ?? '',
    stock_quantity: qty,
    stock_in_quantity: row.stock_in_quantity ?? qty,
    physical_box_no: row.physical_box_no ?? row.physical_box_number ?? '',
    sales_discount_for_bill:
      row.sales_discount_for_bill !== undefined && row.sales_discount_for_bill !== null
        ? row.sales_discount_for_bill
        : '',
    mrp_per_quantity: row.mrp_per_quantity ?? '',
    expiry_alert_period: row.expiry_alert_period ?? '',
    grouping: row.grouping ?? row.group ?? '',
    drug_name: row.drug_name ?? '',
    generic_name: row.generic_name ?? '',
    dosage_type: row.dosage_type ?? '',
    batch_no: row.batch_no ?? '',
    expiry_date: row.expiry_date ?? '',
    reorder_threshold: row.reorder_threshold ?? '',
    hsn_code: row.hsn_code ?? ''
  };
};

const historyQtyDisplay = (v) => {
  if (v === null || v === undefined || v === '') return '-';
  const n = Number(v);
  if (Number.isNaN(n)) return '-';
  return v;
};

function PharmaInventoryAuditLog() {
  const { t } = useTranslation();
  const history = useHistory();
  const isMounted = useRef(true);

  const [userAccess, setUserAccess] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);
  const [rows, setRows] = useState([]);
  const [isLoader, setIsLoader] = useState(false);
  const [isErrorMsg, setIsErrorMsg] = useState(false);
  const [isErrorMsgText, setIsErrorMsgText] = useState('');
  const [successMsg, setSuccessMsg] = useState(false);
  const [successMsgText, setSuccessMsgText] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [viewDrugOpen, setViewDrugOpen] = useState(false);
  const [viewDrugPayload, setViewDrugPayload] = useState({});
  const [searchText, setSearchText] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [batchNo, setBatchNo] = useState('');
  const [includeZero, setIncludeZero] = useState(false);
  const [dosageTypeSelected, setDosageTypeSelected] = useState(null);
  const [appliedBatchNo, setAppliedBatchNo] = useState('');
  const [appliedIncludeZero, setAppliedIncludeZero] = useState(false);
  const [appliedDosageType, setAppliedDosageType] = useState(null);
  const [dosageTypeList, setDosageTypeList] = useState([]);
  const prevFilterOpenRef = useRef(false);
  const [selectedOption, setSelectedOption] = useState('');
  const [historyDataList, setHistoryDataList] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRowId, setMenuRowId] = useState(null);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const msgClose = useCallback(() => {
    setIsErrorMsg(false);
    setIsErrorMsgText('');
    setSuccessMsg(false);
    setSuccessMsgText('');
  }, []);

  const errorMessage = useCallback((message) => {
    setIsErrorMsg(true);
    setIsErrorMsgText(message || '');
  }, []);

  const successMessage = useCallback((message) => {
    setSuccessMsg(true);
    setSuccessMsgText(message || '');
  }, []);

  const getDosageTypeList = useCallback(() => {
    try {
      RestAPIService.getAll(`${Serviceurls.PHARMA_DOSAGE_TYPE_LIST}?with_ids=true`)
        .then((response) => {
          const st = (response.data?.status || '').toLowerCase();
          if (st === 'success') {
            setDosageTypeList(response.data?.data || []);
          }
        })
        .catch(() => {});
    } catch (e) {
      /* ignore */
    }
  }, []);

  const buildListQuery = useCallback(() => {
    const brandName = (searchText || '')
      .replaceAll('&', '%26')
      .replaceAll(',', ' ');
    const dosageType = dosageTypeQueryValue(appliedDosageType);
    const batch = (appliedBatchNo || '').replaceAll('&', '%26');
    return `${Serviceurls.PHARMA_INVENTORY_AUDIT_LOG}?page=${page + 1}&page_size=${pageSize}&brand_name=${brandName}&batch_no=${batch}&dosage_type=${encodeURIComponent(
      dosageType
    )}&include_zero=${appliedIncludeZero}`;
  }, [page, pageSize, searchText, appliedBatchNo, appliedIncludeZero, appliedDosageType]);

  const loadAuditLog = useCallback(() => {
    try {
      setIsLoader(true);
      const url = buildListQuery();
      RestAPIService.getAll(url)
        .then((response) => {
          const st = (response.data?.status || '').toLowerCase();
          if (st === 'success') {
            const payload = response.data?.data || {};
            const result = Array.isArray(payload.result) ? payload.result : [];
            const visible = result.filter((row) => row?.is_audit_zeroed !== true);
            setRows(visible);
            setRowCount(payload.total_count ?? 0);
            setIsLoader(false);
          } else {
            setIsLoader(false);
            errorMessage(response.data?.message || 'Unable to load audit log');
          }
        })
        .catch((error) => {
          setIsLoader(false);
          if (error?.response?.data?.status === 'fail') {
            errorMessage(error.response.data.message);
          } else {
            errorMessage(error?.message || 'Unable to load audit log');
          }
        });
    } catch (e) {
      setIsLoader(false);
      errorMessage(e.message);
    }
  }, [buildListQuery, errorMessage]);

  useEffect(() => {
    let roleData = {};
    try {
      const raw = localGetItem('loggedInUserInfo');
      roleData = raw === null ? {} : JSON.parse(raw);
    } catch (e) {
      roleData = {};
    }
    const ua = CheckAccessFunc(
      roleData?.pharmacy_type === 'Inhouse Pharmacy' ? 'Pharmacy' : 'Standalone Pharmacy',
      'Inventory',
      'Master correction',
      null,
      'Tab'
    );
    if (!ua?.viewAccess) {
      history.replace('/PharmacyInventory/StockList');
      return;
    }
    setUserAccess(ua);
    getDosageTypeList();
  }, [history, getDosageTypeList]);

  useEffect(() => {
    if (!userAccess?.viewAccess) return;
    loadAuditLog();
  }, [
    userAccess,
    page,
    pageSize,
    searchText,
    appliedBatchNo,
    appliedIncludeZero,
    appliedDosageType,
    loadAuditLog
  ]);

  useLayoutEffect(() => {
    if (filterOpen && !prevFilterOpenRef.current) {
      setBatchNo(appliedBatchNo);
      setDosageTypeSelected(appliedDosageType);
      setIncludeZero(appliedIncludeZero);
    }
    prevFilterOpenRef.current = filterOpen;
  }, [filterOpen, appliedBatchNo, appliedDosageType, appliedIncludeZero]);

  const getAuditHistory = useCallback(
    (row) => {
      const stockId = row?.stock_id;
      if (!stockId) return;
      setSelectedOption('History');
      setHistoryLoading(true);
      setHistoryDataList([]);
      try {
        RestAPIService.getAll(
          `${Serviceurls.PHARMA_INVENTORY_AUDIT_LOG_HISTORY}?stock_id=${stockId}`
        )
          .then((response) => {
            if (!isMounted.current) return;
            const st = (response.data?.status || '').toLowerCase();
            if (st !== 'success') {
              setHistoryLoading(false);
              setSelectedOption('');
              errorMessage(response.data?.message || t('Failed'));
              return;
            }
            const root = response.data || {};
            const d = root.data;
            let nextRows = [];

            if (Array.isArray(d)) {
              const sorted = [...d].sort((a, b) => {
                const da = new Date(a?.modified_date || 0).getTime();
                const db = new Date(b?.modified_date || 0).getTime();
                return db - da;
              });
              nextRows = sorted.map((r, idx) => ({
                ...r,
                comments: r.comments ?? r.reason,
                sno: idx + 1,
                rowKey: `${r.stock_list_id ?? ''}-${r.new_stock_list_id ?? ''}-${String(r.modified_date || '')}-${idx}`
              }));
            } else if (d && typeof d === 'object') {
              const rawEvents = Array.isArray(d.events) ? d.events : [];
              const events = sortEventsNewestFirst(rawEvents);
              nextRows = events.map((ev, idx) => ({
                ...ev,
                sno: idx + 1,
                rowKey: `ev-${ev?.event_id ?? ev?.stock_list_id ?? idx}`,
                modified_date:
                  ev?.modified_date || ev?.event_date || ev?.created_date,
                modified_by:
                  ev?.modified_by || ev?.changed_by || ev?.created_by || '-',
                drug_name: ev?.drug_name,
                generic_name: ev?.generic_name,
                dosage_type: ev?.dosage_type,
                drug_strength: ev?.drug_strength,
                schedule: ev?.schedule,
                batch_no: ev?.batch_no,
                expiry_date: ev?.expiry_date,
                from_quantity: ev?.from_quantity,
                adjusted_quantity: ev?.adjusted_quantity,
                to_quantity: ev?.to_quantity,
                mrp_per_quantity: ev?.mrp_per_quantity,
                hsn_code: ev?.hsn_code,
                reorder_threshold: ev?.reorder_threshold,
                expiry_alert_period: ev?.expiry_alert_period,
                physical_box_no: ev?.physical_box_no ?? ev?.physical_box_number,
                group: ev?.group ?? ev?.grouping,
                comments: ev?.comments ?? ev?.reason,
                sales_discount_for_bill: ev?.sales_discount_for_bill
              }));
            }

            if (!isMounted.current) return;
            setHistoryLoading(false);
            setHistoryDataList(nextRows);
          })
          .catch((error) => {
            if (!isMounted.current) return;
            setHistoryLoading(false);
            setSelectedOption('');
            if (error?.response?.data?.message) {
              errorMessage(error.response.data.message);
            } else {
              errorMessage(error?.message || t('Failed'));
            }
          });
      } catch (e) {
        if (isMounted.current) {
          setHistoryLoading(false);
          setSelectedOption('');
          errorMessage(e.message);
        }
      }
    },
    [t, errorMessage]
  );

  const stockHistoryClose = useCallback(() => {
    setSelectedOption('');
    setHistoryDataList([]);
    setHistoryLoading(false);
    setAnchorEl(null);
    setMenuRowId(null);
  }, []);

  const auditHistoryMenu = useCallback(
    (data) => {
      const options = [{ value: 'History', label: t('History') }];
      return (
        <div>
          <Tooltip title={t('More')} placement="top" arrow>
            <IconButton
              emed_tid="inv_mc_morevert"
              onClick={(e) => {
                setAnchorEl(e.currentTarget);
                setMenuRowId(data.stock_id);
              }}
            >
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
          {menuRowId === data.stock_id ? (
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
            >
              {options.map((option) => (
                <MenuItem
                  key={option.value}
                  emed_tid={`inv_mc_menu_${option.value}`}
                  onClick={() => {
                    setAnchorEl(null);
                    setSelectedOption(option.value);
                    getAuditHistory(data);
                  }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Menu>
          ) : null}
        </div>
      );
    },
    [anchorEl, menuRowId, t, getAuditHistory]
  );

  const renderStockHistory = useCallback(() => {
    if (historyLoading) {
      return (
        <Box
          id="eMed_pharma_inv_audit_hist"
          sx={{
            width: '100%',
            minWidth: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 240
          }}
        >
          <CircularProgress size={32} />
        </Box>
      );
    }
    const list = (historyDataList || []).map((r, i) => ({
      ...r,
      sno: i + 1
    }));

    const historyColumn = [
      {
        field: 'modified_date',
        headerName: t('Updated Date & Time'),
        sortable: false,
        hideable: false,
        width: 220,
        headerAlign: 'center',
        align: 'center',
        renderCell: ({ row }) => (
          <Box component="div">
            {row?.modified_date ? formatModifiedDateTimeWithPipe(row?.modified_date) : '-'}
          </Box>
        )
      },
      {
        field: 'modified_by',
        headerName: t('UpdatedBy'),
        width: 300,
        sortable: false,
        hideable: false,
        renderCell: ({ row }) => (
          <HistoryTruncatedCell value={row?.modified_by} />
        )
      },
      {
        field: 'audit_changed_fields',
        headerName: t('Changes Updated'),
        width: 218,
        sortable: false,
        hideable: false,
        renderCell: ({ row }) => {
          const lines = changedFieldsLines(row.changed_fields);
          if (lines.length === 0) {
            return (
              <Box sx={{ py: 0.25 }}>-</Box>
            );
          }
          const previewSlice = lines.slice(0, HISTORY_CHANGED_PREVIEW_MAX_LINES);
          const hasHiddenLines = lines.length > HISTORY_CHANGED_PREVIEW_MAX_LINES;
          const compactFont = lines.length > 2;
          const anyLineLong = previewSlice.some(
            (line) => line.length > HISTORY_CHANGED_TOOLTIP_MIN_LINE_CHARS
          );
          const needsTooltip = hasHiddenLines || anyLineLong;

          const lineSx = {
            lineHeight: 1.35,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            ...(compactFont ? { fontSize: '0.78rem' } : {})
          };

          const cell = (
            <Box
              sx={{
                maxWidth: '100%',
                maxHeight: compactFont ? '4.2em' : 'none',
                overflow: 'hidden',
                py: 0.25,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: compactFont ? 0.15 : 0.25
              }}
            >
              {previewSlice.map((line, i) =>
                compactFont ? (
                  <Typography key={i} variant="body2" sx={lineSx}>
                    {line}
                  </Typography>
                ) : (
                  <Box key={i} component="div" sx={lineSx}>
                    {line}
                  </Box>
                )
              )}
              {hasHiddenLines ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', lineHeight: 1.2, mt: 0.1 }}
                >
                  …
                </Typography>
              ) : null}
            </Box>
          );

          return needsTooltip ? (
            <Tooltip
              title={<WhatChangedBulletedTooltip lines={lines} />}
              placement="left"
              arrow
              componentsProps={{
                tooltip: { sx: { maxWidth: 520 } }
              }}
            >
              {cell}
            </Tooltip>
          ) : (
            cell
          );
        }
      },
      {
        field: 'drug_name',
        headerName: t('BrandName'),
        width: 218,
        sortable: false,
        hideable: false,
        renderCell: ({ row }) => <HistoryTruncatedCell value={row?.drug_name} />
      },
      {
        field: 'generic_name',
        headerName: t('GenericName'),
        sortable: false,
        hideable: false,
        width: 218,
        renderCell: ({ row }) => <HistoryTruncatedCell value={row?.generic_name} />
      },
      {
        field: 'dosage_type',
        headerName: t('DosageType'),
        sortable: false,
        hideable: false,
        width: 210,
        renderCell: ({ row }) => <HistoryTruncatedCell value={row?.dosage_type} />
      },
      {
        field: 'drug_strength',
        headerName: t('DosageStrength'),
        sortable: false,
        hideable: false,
        width: 200,
        renderCell: ({ row }) => <HistoryTruncatedCell value={row?.drug_strength} />
      },
      {
        field: 'schedule',
        headerName: t('Schedule'),
        width: 200,
        sortable: false,
        hideable: false,
        renderCell: ({ row }) => <HistoryTruncatedCell value={row?.schedule} />
      },
      {
        field: 'batch_no',
        headerName: t('BatchNo'),
        width: 218,
        sortable: false,
        hideable: false,
        headerAlign: 'center',
        align: 'center',
        renderCell: ({ row }) => (
          <Box sx={{ width: '100%', textAlign: 'center' }}>
            <HistoryTruncatedCell value={row?.batch_no} />
          </Box>
        )
      },
      {
        field: 'expiry_date',
        headerName: t('ExpiryDate'),
        width: 218,
        sortable: false,
        hideable: false,
        headerAlign: 'center',
        align: 'center',
        renderCell: ({ row }) => (
          <Box component="div">{row?.expiry_date ? row?.expiry_date : '-'}</Box>
        )
      },
      {
        field: 'from_quantity',
        headerName: t('PrevQty'),
        width: 115,
        sortable: false,
        hideable: false,
        type: 'number',
        renderCell: ({ row }) => <div>{historyQtyDisplay(row?.from_quantity)}</div>
      },
      {
        field: 'adjusted_quantity',
        headerName: t('AdjQty'),
        width: 115,
        sortable: false,
        hideable: false,
        type: 'number',
        renderCell: ({ row }) => <div>{historyQtyDisplay(row?.adjusted_quantity)}</div>
      },
      {
        field: 'to_quantity',
        headerName: t('AvailableQty'),
        width: 115,
        sortable: false,
        hideable: false,
        type: 'number',
        renderCell: ({ row }) => <div>{historyQtyDisplay(row?.to_quantity)}</div>
      },
      {
        field: 'mrp_per_quantity',
        headerName: `${t('MRP/Qty')} (${CurrencySymbol})`,
        width: 125,
        sortable: false,
        hideable: false,
        type: 'number',
        renderCell: ({ row }) => (
          <div>
            {AmountFormat(row?.mrp_per_quantity || 0)?.replace(`${CurrencySymbol}`, '')}
          </div>
        )
      },
      {
        field: 'hsn_code',
        headerName: t('HSNCode'),
        width: 200,
        sortable: false,
        hideable: false,
        type: 'number',
        renderCell: ({ row }) => <HistoryTruncatedCell value={row?.hsn_code} />
      },
      {
        field: 'reorder_threshold',
        headerName: t('ReorderThreshold'),
        width: 168,
        sortable: false,
        hideable: false,
        type: 'number',
        renderCell: ({ row }) => <HistoryTruncatedCell value={row?.reorder_threshold} />
      },
      {
        field: 'expiry_alert_period',
        headerName: t('ExpiryPeriod'),
        width: 168,
        sortable: false,
        hideable: false,
        type: 'number',
        renderCell: ({ row }) => <HistoryTruncatedCell value={row?.expiry_alert_period} />
      },
      {
        field: 'physical_box_no',
        headerName: t('PhyBoxNo'),
        width: 200,
        sortable: false,
        hideable: false,
        type: 'number',
        renderCell: ({ row }) => <HistoryTruncatedCell value={row?.physical_box_no} />
      },
      {
        field: 'group',
        headerName: t('Grouping'),
        width: 200,
        sortable: false,
        hideable: false,
        type: 'number',
        renderCell: ({ row }) => <HistoryTruncatedCell value={row?.group} />
      },
      {
        field: 'sales_discount_for_bill',
        headerName: t('SalesDiscount%'),
        width: 155,
        sortable: false,
        hideable: false,
        type: 'number',
        renderCell: ({ row }) => (
          <div>{historyQtyDisplay(row?.sales_discount_for_bill)}</div>
        )
      },
      {
        field: 'comments',
        headerName: t('Reason'),
        width: 218,
        sortable: false,
        hideable: false,
        headerAlign: 'center',
        align: 'center',
        renderCell: ({ row }) => (
          <Box sx={{ width: '100%', textAlign: 'center' }}>
            <HistoryTruncatedCell value={row?.comments} />
          </Box>
        )
      }
    ];

    return (
      <Box
        id="eMed_pharma_inv_audit_hist"
        sx={{
          width: '100%',
          minWidth: 0,
          minHeight: 240,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          boxSizing: 'border-box'
        }}
      >
        <Box
          id="eMed_pharma_settings_stkTbl"
          sx={{ width: '100%', minWidth: 0, flex: '1 1 auto' }}
        >
          <DataGrid
            rows={list}
            columns={historyColumn}
            getRowId={(row) =>
              String(row.rowKey ?? row.sno ?? row.stock_list_id ?? row.event_id)
            }
            disableColumnMenu
            hideFooter
            sx={{
              width: '100%',
              minWidth: 0,
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 600,
                lineHeight: 1.25,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                overflow: 'visible'
              },
              '& .MuiDataGrid-columnHeader': {
                py: 1,
                alignItems: 'flex-start'
              },
              '& .MuiDataGrid-cell': {
                py: 0.75,
                px: 0.75
              }
            }}
            components={{
              NoRowsOverlay: () => (
                <Stack className="eMed_conf_nodata">{t('NoRecordsFound')}</Stack>
              )
            }}
            headerHeight={56}
          />
        </Box>
      </Box>
    );
  }, [historyLoading, historyDataList, t]);

  const rowsWithSerial = useMemo(
    () =>
      rows.map((element, index) => ({
        ...element,
        serial: page * pageSize + index + 1
      })),
    [rows, page, pageSize]
  );

  const mainColumns = useMemo(
    () => [
      {
        field: 'serial',
        headerName: t('SNo'),
        sortable: false,
        flex: 0.04,
        headerAlign: 'center',
        align: 'center',
        renderCell: (params) => (
          <Box component="div">{params?.row?.serial ?? '-'}</Box>
        )
      },
      {
        field: 'drug_details',
        headerName: t('DrugDetails'),
        flex: 0.16,
        sortable: false,
        renderCell: (params) => {
          const row = params.row;
          const dosageTypeHSN = `${row?.dosage_type || '-'} | ${row?.hsn_code || '-'}`;
          const dosageTypeHSNTooltipTitle = (
            <Box sx={{ py: 0.25 }}>
              <Typography variant="body2" component="div" sx={{ fontWeight: 600, mb: 0.25 }}>
                Dosage Type
              </Typography>
              <Typography variant="body2" component="div">
                {row?.dosage_type || '-'}
              </Typography>
              <Typography variant="body2" component="div" sx={{ fontWeight: 600, mt: 0.75, mb: 0.25 }}>
                HSN Code
              </Typography>
              <Typography variant="body2" component="div">
                {row?.hsn_code || '-'}
              </Typography>
            </Box>
          );
          return (
            <Box component="div">
              <Typography fontSize="0.8vw" fontWeight={600}>
                {row?.drug_name || '-'}
              </Typography>
              <Typography fontSize="0.8vw">{row?.generic_name || '-'}</Typography>
              <Box>
                {dosageTypeHSN.length > 18 ? (
                  <Tooltip
                    placement="right"
                    title={dosageTypeHSNTooltipTitle}
                    arrow
                    componentsProps={{
                      tooltip: { sx: { maxWidth: 280 } }
                    }}
                  >
                    <Box component="span" sx={{ display: 'block', maxWidth: '100%', cursor: 'default' }}>
                      <Typography fontSize="0.8vw" component="span" display="block">
                        {`${dosageTypeHSN.slice(0, 18)}...`}
                      </Typography>
                    </Box>
                  </Tooltip>
                ) : (
                  <Typography fontSize="0.8vw">{dosageTypeHSN}</Typography>
                )}
              </Box>
            </Box>
          );
        },
        valueGetter: ({ row }) => {
          const hsncode = row?.hsn_code || '';
          const drugName = row?.drug_name || '';
          const genericName = row?.generic_name || '';
          const strength = row?.dosage_type || '';
          return `${hsncode} ${genericName} ${strength} ${drugName}`;
        }
      },
      { field: 'strength', headerName: t('Strength'), flex: 0.07 },
      {
        field: 'schedule',
        headerName: t('Schedule'),
        flex: 0.06,
        sortable: false,
        renderCell: (params) => {
          const raw = params.row?.schedule;
          const s =
            raw != null && raw !== undefined && String(raw).trim() !== ''
              ? String(raw)
              : '';
          if (!s) {
            return (
              <Box sx={{ width: '100%' }}>-</Box>
            );
          }
          const maxDisplayChars = 8;
          const displayTruncated = s.length > maxDisplayChars;
          const shown = displayTruncated
            ? `${s.slice(0, maxDisplayChars)}…`
            : s;
          const trigger = (
            <span
              style={{
                display: 'block',
                width: '100%',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                cursor: 'default'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {shown}
            </span>
          );
          if (!displayTruncated) {
            return trigger;
          }
          return (
            <Tooltip
              title={s}
              placement="top"
              arrow
              enterDelay={200}
              PopperProps={{
                style: { zIndex: 2000 }
              }}
              componentsProps={{
                tooltip: { sx: { maxWidth: 360 } }
              }}
            >
              {trigger}
            </Tooltip>
          );
        }
      },
      { field: 'batch_no', headerName: t('BatchNo'), flex: 0.09 },
      { field: 'expiry_date', headerName: t('ExpiryDate'), flex: 0.1 },
      {
        field: 'available_quantity',
        headerName: t('Available Qty'),
        flex: 0.08,
      },
      {
        field: 'mrp_per_quantity',
        headerName: t(`MRP/Qty (${CurrencySymbol})`),
        flex: 0.1,
        type: 'number',
        renderCell: (params) => (
          <div>
            {AmountFormat(params?.row?.mrp_per_quantity || 0)?.replace(
              `${CurrencySymbol}`,
              ''
            )}
          </div>
        )
      },
      {
        field: 'reorder_threshold',
        headerName: t('ReorderThreshold'),
        flex: 0.1,
        type: 'number'
      },
      {
        field: 'action',
        headerName: t('Action'),
        sortable: false,
        flex: 0.1,
        headerAlign: 'center',
        align: 'center',
        renderCell: (params) => (
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
          >
            {userAccess?.editAccess ? (
              <Tooltip title={t('Edit Durg')} placement="top" arrow>
                <Button
                  emed_tid={`inv_audit_edit_${params.row.stock_id}`}
                  className="eMed_usrconf_btn"
                  onClick={() => {
                    setEditOpen(true);
                    setEditRow(params.row);
                  }}
                >
                  <img
                    src={ImagePaths.LabEditButton.default}
                    alt="edit"
                    className="eMed_action_img"
                  />
                </Button>
              </Tooltip>
            ) : null}
            <Tooltip title={t('View Drug')} placement="top" arrow>
              <Button
                emed_tid={`inv_mc_view_${params.row.stock_id}`}
                className="eMed_usrconf_btn"
                onClick={() => {
                  setViewDrugOpen(true);
                  setViewDrugPayload(mapAuditRowForViewDrug(params.row));
                }}
              >
                <img
                  src={ImagePaths.LabViewButton.default}
                  alt="view"
                  className="eMed_action_img"
                />
              </Button>
            </Tooltip>
            {auditHistoryMenu(params.row)}
          </Box>
        )
      }
    ],
    [t, userAccess, auditHistoryMenu]
  );

  const renderFilterScreen = () => (
    <Box component="div" className="eMed_rts_filter_container">
      <Box component="div" className="eMed_filter_Top">
        <Typography variant="h6" fontWeight={600}>
          {t('Filter')}
        </Typography>
        <Button onClick={() => setFilterOpen(false)} className="eMed_usrconf_btn">
          <img
            src={ImagePaths.LabCloseButton.default}
            alt="close"
            className="eMed_action_img_small"
          />
        </Button>
      </Box>
      <Box component="div" p="2vw 1vw 1vw 1vw" maxHeight="32vw" overflow="auto">
        <Stack spacing={2}>
          <Autocomplete
            size="small"
            sx={{ width: '20vw' }}
            options={dosageTypeList}
            getOptionLabel={(option) => dosageOptionLabel(option)}
            value={dosageTypeSelected}
            onChange={(e, newValue) => {
              setDosageTypeSelected(newValue);
              if (!newValue && batchNo === '') setIncludeZero(false);
            }}
            renderInput={(params) => <TextField {...params} label={t('DosageType')} />}
          />
          <TextField
            size="small"
            sx={{ width: '20vw' }}
            label={t('BatchNo')}
            placeholder={t('BatchNo')}
            autoComplete="off"
            value={batchNo}
            onChange={(e) => {
              const v = e.target.value;
              setBatchNo(v);
              if (v === '' && !dosageTypeSelected) setIncludeZero(false);
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                disabled={batchNo === '' && !dosageTypeSelected}
                checked={includeZero}
                onChange={(e) => setIncludeZero(e.target.checked)}
              />
            }
            label="Search Include Zero Quantity"
          />
        </Stack>
      </Box>
      <Box component="div" display="flex" justifyContent="center">
        <Button
          variant="outlined"
          sx={{ height: '2vw', mr: '1vw', textTransform: 'capitalize' }}
          onClick={() => {
            setBatchNo('');
            setIncludeZero(false);
            setDosageTypeSelected(null);
            setAppliedBatchNo('');
            setAppliedIncludeZero(false);
            setAppliedDosageType(null);
            setFilterOpen(false);
            setPage(0);
          }}
        >
          {t('Clear')}
        </Button>
        <Button
          variant="contained"
          sx={{ height: '2vw', mr: '1vw', textTransform: 'capitalize' }}
          onClick={() => {
            setAppliedBatchNo(batchNo);
            setAppliedIncludeZero(includeZero);
            setAppliedDosageType(dosageTypeSelected);
            setFilterOpen(false);
            setPage(0);
          }}
        >
          {t('Search')}
        </Button>
      </Box>
    </Box>
  );

  if (!userAccess?.viewAccess) {
    return null;
  }

  return (
    <div>
      <div
        className="eMed_Purchase_Table_top"
        style={{ justifyContent: 'space-between' }}
      >
        <div />
        <div
          className="eMed_Purchase_Table_top"
          style={{ justifyContent: 'space-between' }}
        >
          <Box className="eMed_Search_Box_">
            <TextField
              variant="standard"
              placeholder="Search brand name"
              value={searchText}
              onChange={(e) => {
                const v = e.target.value;
                setSearchText(v);
                if (v?.length >= 2 || v?.length === 0) {
                  setPage(0);
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              inputProps={{ emed_tid: 'inv_audit_drug_search' }}
              sx={{ width: '15vw' }}
            />
            <Box component="div">
              <Button
                className="Common_Btn_Min_Width"
                sx={{ height: '2vw', width: '2vw', ml: '0.5vw' }}
                onClick={() => setFilterOpen(true)}
              >
                <Box
                  component="img"
                  src={ImagePaths.Filter.default}
                  height="2vw"
                  width="2vw"
                />
              </Button>
            </Box>
          </Box>
        </div>
      </div>
      <Box component="div" className="eMed_Purcahse_Table_Container">
        <Stack component="div">
          <Paper className="eMed_Purchase_Table_Wrapper">
            <DataGrid
              rows={rowsWithSerial}
              columns={mainColumns}
              getRowId={(row) => row.stock_id}
              page={page}
              pageSize={pageSize}
              hideFooterSelectedRowCount
              localeText={{
                toolbarColumns: '',
                toolbarDensity: '',
                toolbarExport: '',
                toolbarFilters: '',
                toolbarExportPrint: ''
              }}
              headerHeight={40}
              components={{
                NoRowsOverlay: () => (
                  <Stack className="eMed_conf_nodata">{t('NoRecordsFound')}</Stack>
                )
              }}
              rowsPerPageOptions={[10, 20, 30]}
              disableSelectionOnClick
              pagination
              paginationMode="server"
              rowCount={rowCount}
              onPageChange={(newPage) => {
                setPage(newPage);
              }}
              onPageSizeChange={(newPageSize) => {
                setPage(0);
                setPageSize(newPageSize);
              }}
              loading={isLoader}
              density="comfortable"
            />
          </Paper>
        </Stack>
      </Box>
      <PharmaInventoryAuditLogEditModal
        open={editOpen}
        initialRow={editRow}
        onClose={() => {
          setEditOpen(false);
          setEditRow(null);
        }}
        onSuccess={(msg) => {
          successMessage(msg);
          loadAuditLog();
        }}
        onError={errorMessage}
        editAccess={!!userAccess?.editAccess}
      />
      {selectedOption === 'History' ? (
        <CommonPopUp
          width="90vw"
          backgroundColor={Colors.white}
          popUpClose={stockHistoryClose}
          title={t('History')}
          component={renderStockHistory}
        />
      ) : null}
      {viewDrugOpen ? (
        <Modal open={viewDrugOpen} onClose={() => setViewDrugOpen(false)}>
          <EditStockPopUp
            SelectedDurg={JSON.stringify(viewDrugPayload)}
            PopUpclose={() => setViewDrugOpen(false)}
            isForViewOnly
          />
        </Modal>
      ) : null}
      {isErrorMsg ? (
        <ToastMsg
          severity="error"
          msg={isErrorMsgText}
          msgPop={msgClose}
        />
      ) : null}
      {successMsg ? (
        <ToastMsg
          severity="success"
          msg={successMsgText}
          msgPop={msgClose}
        />
      ) : null}
      <Drawer
        anchor="right"
        open={filterOpen}
        ModalProps={{
          onBackdropClick: () => setFilterOpen(false)
        }}
      >
        {renderFilterScreen()}
      </Drawer>
    </div>
  );
}

export default PharmaInventoryAuditLog;
